<template>
  <div class="w-100">
    <h2 class="mt-0">
      Activities
    </h2>
    <ul class="list">
      <div class="row d-none d-md-flex border-bottom">
        <span
          v-for="column in columns"
          :key="column.name"
          :style="getColumnWidth(column.name)"
          :class="column.name === 'distance' ? 'text-md-right' : 'text-left'"
        >
          <button
            v-if="column !== ''"
            class="btn btn-tertiary__trans select pl-3 pr-3 pt-2 pb-2 text-capitalize text-left"
            @click="sortByColumn($event, column)"
          >
            {{ column.name.split('_')[1] ? column.name.split('_')[1] : column.name }}
          </button>
        </span>
      </div>
      <li
        v-for="index in activitiesToShow"
        :key="index"
        class="list-group-item d-none d-md-flex mb-1 p-0 flex-wrap border-bottom"
      >
        <a
          class="w-100 row"
          @click="e => e.target.closest('.list-group-item').classList.toggle('active')"
        >
          <div
            v-for="column in columns"
            :key="column.name"
            :style="getColumnWidth(column.name)"
            :class="['pl-3 pr-3 pt-2 pb-2 activity-name', column.name === 'distance' ? 'font-weight-bold text-right' : '']"
          >
            <span v-if="column.name === 'activity_type'" class="text-capitalize">
              {{ sortedActivities[index - 1][column.name] }}
            </span>
            <span v-else-if="column.name === 'distance'">
              {{ parseFloat(sortedActivities[index - 1][column.name] / 1609.344).toFixed(2) }}<small>mi</small>
            </span>
            <span v-else-if="column.name === 'user'" class="athlete-name">
              {{ sortedActivities[index - 1][column.name].username }}
            </span>
            <span v-else class="text-muted">
              {{ sortedActivities[index - 1][column.name] }}
            </span>
          </div>
          <span class="row w-100 pl-3 pr-2 pb-2 align-items-center justify-content-between stats">
            <small>{{ new Date(sortedActivities[index - 1].start_date).toLocaleDateString() }}</small>
            <span class="row">
              <span class="btn btn__pill row align-items-center">
                <svg-icon
                  icon="mountains"
                  fill-color="#6E7C8"
                  width="20px"
                  height="20px"
                  stroke-color="none"
                />
                <span class="pl-1">{{ parseFloat(sortedActivities[index - 1].elevation_gain * 3.28084).toFixed(0) }}<small>ft</small></span>
              </span>
              <span class="btn btn__pill row align-items-center ml-2">
                <svg-icon
                  icon="time"
                  fill-color="#6E7C8"
                  width="20px"
                  height="20px"
                  stroke-color="none"
                />
                <span class="pl-1">{{ formatSecondsToHms(sortedActivities[index - 1].moving_time) }}</span>
              </span>
            </span>
          </span>
        </a>
      </li>
      <li
        v-for="index in activitiesToShow"
        :key="'mobile-' + index"
        class="list-group-item d-flex d-md-none mb-1 flex-wrap border-bottom"
      >
        <a
          class="w-100 row"
          @click="e => e.target.closest('.list-group-item').classList.toggle('active')"
        >
          <span class="athlete-name col-8 p-0">{{ sortedActivities[index - 1].user.username }}</span>
          <span class="col-4 p-0 justify-content-end row">
            <strong>{{ parseFloat(sortedActivities[index - 1].distance / 1609.344).toFixed(2) }}</strong><small>mi</small>
          </span>
          <span class="col-10 p-0 mt-2 text-muted activity-name">{{ sortedActivities[index - 1].activity_name }}</span>
          <span class="col-2 mt-1 p-0">
            <svg-icon
              :icon="sortedActivities[index - 1].activity_type"
              width="14px"
              height="14px"
              fill-color="#174776"
              stroke-color="none"
              classes="text-right"
            />
          </span>
          <span class="row w-100 pt-2 align-items-center stats">
            <small class="mb-2">{{ new Date(sortedActivities[index - 1].start_date).toLocaleDateString() }}</small>
            <span class="w-100 row justify-content-between">
              <span class="btn btn__pill row align-items-center">
                <svg-icon
                  icon="mountains"
                  fill-color="#6E7C8"
                  width="20px"
                  height="20px"
                  stroke-color="none"
                />
                <span class="pl-1">{{ parseFloat(sortedActivities[index - 1].elevation_gain * 3.28084).toFixed(0) }}<small>ft</small></span>
              </span>
              <span class="btn btn__pill row align-items-center ml-2">
                <svg-icon
                  icon="time"
                  fill-color="#6E7C8"
                  width="20px"
                  height="20px"
                  stroke-color="none"
                />
                <span class="pl-1">{{ formatSecondsToHms(sortedActivities[index - 1].moving_time) }}</span>
              </span>
            </span>
          </span>
        </a>
      </li>
    </ul>
    <span v-if="sortedActivities.length > activitiesToShow" class="w-100 text-center d-block">
      <button class="btn btn-tertiary__trans" @click="showMore">SHOW MORE</button>
    </span>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import SvgIcon from './svg-icon.vue';
import { secondsToHms } from '../composables/useFormatters.js';
import { applyOverrideDistances } from '../composables/useAthleteData.js';

const props = defineProps({
  activities: {
    type: Array,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
});

const sortedActivities = ref([...props.activities]);
const activitiesToShow = ref(props.activities.length < 10 ? props.activities.length : 10);

onMounted(() => {
  applyOverrideDistances(sortedActivities.value);
});

function formatSecondsToHms(d) {
  return secondsToHms(d);
}

function showMore() {
  if (activitiesToShow.value + 5 > sortedActivities.value.length) {
    activitiesToShow.value = sortedActivities.value.length;
  } else {
    activitiesToShow.value += 5;
  }
}

function getColumnWidth(key) {
  let count = 0;
  for (const col of props.columns) {
    count += col.span;
  }

  let width = 100 / count;
  for (const col of props.columns) {
    if (col.name === key) {
      width = width * col.span;
    }
  }
  return { width: width + '%' };
}

function dynamicSort(property, sortOrder) {
  return function(a, b) {
    const testFloat = parseFloat(a[property]);
    let result;
    if (isNaN(testFloat)) {
      result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
    } else {
      const _a = parseFloat(a[property]);
      const _b = parseFloat(b[property]);
      result = _a < _b ? -1 : _a > _b ? 1 : 0;
    }
    return result * sortOrder;
  };
}

function sortByColumn(event, column) {
  let sortOrder = -1;
  const targetElm = event.currentTarget;
  const sortButtons = document.querySelectorAll('.list-group .btn.select');

  sortButtons.forEach((btn) => {
    if (btn !== targetElm) {
      btn.classList.remove('asc', 'desc');
    }
  });

  if (targetElm.classList.contains('asc') || targetElm.classList.contains('desc')) {
    if (targetElm.classList.contains('asc')) {
      targetElm.classList.remove('asc');
      targetElm.classList.add('desc');
      sortOrder = 1;
    } else if (targetElm.classList.contains('desc')) {
      targetElm.classList.remove('desc');
      targetElm.classList.add('asc');
      sortOrder = -1;
    }
  } else {
    targetElm.classList.add('asc');
  }

  sortedActivities.value = [...sortedActivities.value].sort(dynamicSort(column.name, sortOrder));
}
</script>

<style lang="scss" scoped>
.athlete-name {
  text-transform: capitalize;
}
.activity-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.list-group-item {
  cursor: pointer;
}
.list-group-item:not(.active) .stats {
  display: none;
}
</style>
